import { Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useIsWorkplaceReviewUXUpdateEnabled } from "@src/appV2/PostShiftReview/hooks/useIsWorkplaceReviewUXUpdateEnabled";
import { Button } from "@src/appV2/redesign/components/Button";
import {
  SHIFT_DISCOVERY_UNLOCK_REVIEWS_MODAL_PATH,
  SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH,
} from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useWorkplaceModalsDataContext } from "@src/appV2/redesign/ShiftDiscovery/useWorkplaceModalsDataContext";
import { Input, Text } from "@src/appV2/redesign/system/ui-components";
import {
  type PaginatedWorkplaceCommentsParams,
  usePaginatedWorkplaceComments,
} from "@src/appV2/redesign/Workplace/Review/api/usePaginatedWorkplaceComments";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { parseISO } from "date-fns";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { UNLOCK_REVIEWS_ORIGIN } from "../UnlockReviews/navigation";
import { isReviewRedacted, isViewerAuthor } from "../utils";
import { WorkplaceReviewCommentCard } from "./Card";
import { COMMENTS_PAGE_SIZE, LEGACY_COMMENTS_PAGE_SIZE } from "./constants";
import { InlineRepliesSection } from "./InlineRepliesSection";
import { InlineReplyInput } from "./InlineReplyInput";
import { useUpdateCachedComment } from "./useUpdateCachedComment";

interface WorkplaceCommentListProps {
  workplaceId: string;
  onBeforeNavigateToComment?: () => void;
}

export function WorkplaceCommentList(props: WorkplaceCommentListProps) {
  const { workplaceId, onBeforeNavigateToComment } = props;

  const history = useHistory();
  const worker = useDefinedWorker();
  const { isEnabled: isReviewUXUpdateEnabled } = useIsWorkplaceReviewUXUpdateEnabled();

  const { generateModalPath, reviewsViewContext } = useWorkplaceModalsDataContext();

  function handleUnlockPress() {
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_UNLOCK_CTA_TAPPED, { workplaceId });
    history.push(generateModalPath(SHIFT_DISCOVERY_UNLOCK_REVIEWS_MODAL_PATH, { workplaceId }), {
      unlockReviewsOrigin: UNLOCK_REVIEWS_ORIGIN.REVIEWS_MODAL,
    });
  }

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const trimmedSearchTerm = searchTerm.trim();

  const commentsPageSize = isReviewUXUpdateEnabled ? COMMENTS_PAGE_SIZE : LEGACY_COMMENTS_PAGE_SIZE;
  const backendSearchText =
    isReviewUXUpdateEnabled && trimmedSearchTerm ? trimmedSearchTerm.slice(0, 255) : undefined;
  const commentsParams: PaginatedWorkplaceCommentsParams = {
    workplaceId,
    page: { size: commentsPageSize },
    filter: isReviewUXUpdateEnabled
      ? { sort: "-relevancyScore", searchText: backendSearchText }
      : undefined,
    viewContext: reviewsViewContext,
  };
  const {
    data: commentsData,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = usePaginatedWorkplaceComments(commentsParams, {
    keepPreviousData: isReviewUXUpdateEnabled,
  });

  const allComments = useMemo(() => {
    if (!commentsData) {
      return [];
    }

    return commentsData.pages.flatMap((page) => page.data);
  }, [commentsData]);

  const filteredComments = useMemo(() => {
    const sortedComments = isReviewUXUpdateEnabled
      ? allComments
      : allComments.toSorted((a, b) => {
          return (
            parseISO(b.attributes.createdAt).getTime() - parseISO(a.attributes.createdAt).getTime()
          );
        });

    if (isReviewUXUpdateEnabled) {
      return sortedComments;
    }

    if (!searchTerm) {
      return sortedComments;
    }

    const lowercaseSearch = searchTerm.toLowerCase();
    return sortedComments.filter((comment) =>
      comment.attributes.text.toLowerCase().includes(lowercaseSearch)
    );
  }, [allComments, searchTerm, isReviewUXUpdateEnabled]);

  const hasActiveSearch = isReviewUXUpdateEnabled && Boolean(trimmedSearchTerm);
  const showNoReviewsYet = allComments.length === 0 && !hasActiveSearch;
  const showNoMatchingResults =
    filteredComments.length === 0 && (hasActiveSearch || allComments.length > 0);

  const logSearchEvent = useCallback(
    (term: string) => {
      const trimmedTerm = term.trim();
      if (trimmedTerm) {
        logEvent(APP_V2_USER_EVENTS.WORKPLACE_REVIEWS_SEARCHED, {
          workplaceId,
          searchText: trimmedTerm,
          searchMode: isReviewUXUpdateEnabled ? "backend" : "frontend",
        });
      }
    },
    [workplaceId, isReviewUXUpdateEnabled]
  );

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term);
        logSearchEvent(term);
      }, 300),
    [logSearchEvent]
  );

  useEffect(() => {
    return () => {
      debouncedSetSearchTerm.cancel();
    };
  }, [debouncedSetSearchTerm]);

  const { updateComment, deleteComment } = useUpdateCachedComment({
    workplaceId: String(workplaceId),
    commentsParams,
  });

  return (
    <Stack spacing={7}>
      <Text semibold variant="h6" sx={{ paddingLeft: 5 }}>
        Reviews
      </Text>

      <Input
        ref={inputRef}
        iconType="search"
        placeholder="Search reviews..."
        size="small"
        onChange={(event) => {
          debouncedSetSearchTerm(event.target.value);
        }}
      />

      <Stack spacing={3}>
        {isLoading && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            Loading reviews...
          </Text>
        )}

        {!isLoading && showNoReviewsYet && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            No reviews yet.
          </Text>
        )}

        {!isLoading && showNoMatchingResults && (
          <Text variant="h6" sx={{ paddingTop: 7, textAlign: "center" }}>
            No reviews matching your search
          </Text>
        )}

        {filteredComments.map((comment) => (
          <WorkplaceReviewCommentCard
            key={comment.id}
            comment={comment}
            workplaceId={String(workplaceId)}
            isLocked={isReviewRedacted(comment)}
            viewerIsAuthor={isViewerAuthor({
              workerInfo: comment.attributes.workerInfo,
              viewerUserId: worker.userId,
            })}
            onUnlockPress={() => handleUnlockPress()}
            onClick={() => {
              onBeforeNavigateToComment?.();
              history.push(
                generateModalPath(SHIFT_DISCOVERY_WORKPLACE_REVIEWS_COMMENT_MODAL_PATH, {
                  workplaceId,
                  commentId: comment.id,
                })
              );
            }}
            onLikeChanged={(newComment) => {
              updateComment(newComment);
            }}
            onAnonymized={(newComment) => {
              updateComment(newComment);
            }}
            onRemoved={(commentId) => {
              deleteComment(commentId);
            }}
          >
            {isReviewUXUpdateEnabled && !isReviewRedacted(comment) && (
              <>
                <InlineRepliesSection
                  parentComment={comment}
                  workplaceId={workplaceId}
                  viewerUserId={worker.userId}
                />
                <InlineReplyInput
                  workplaceId={workplaceId}
                  parentId={comment.id}
                  workerFullName={worker.name}
                />
              </>
            )}
          </WorkplaceReviewCommentCard>
        ))}

        {hasNextPage && (
          <Button
            variant="outlined"
            disabled={isFetchingNextPage}
            sx={{ alignSelf: "center" }}
            onClick={async () => {
              await fetchNextPage();
            }}
          >
            {isFetchingNextPage ? "Loading more..." : "Load more reviews"}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
